import axios from 'axios';
import { Product } from './products';

// NOTE: In a production environment, this key should be in a .env file
// and preferably requests should be proxied through a backend to avoid exposing the key.
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || 'AIzaSyA13nCCY6Hvc-vmnRfAnAxL2UzmhhhJYWs';
// Fallback to gemini-pro which is generally available
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${GEMINI_API_KEY}`;

// Define response interface
export interface GeminiResponse {
    text: string;
    handoffSummary?: string;
    suggestions?: { title: string, url: string }[];
}

export const generateGeminiResponse = async (
    userMessage: string,
    products: Product[],
    language: string
): Promise<GeminiResponse> => {
    try {
        // Prepare context from products
        const productContext = products.map(p => {
            const price = p.discount_price || p.price;
            return `- ${language === 'fr' ? p.title_fr : p.title_ar} (${price} MAD): ${language === 'fr' ? p.short_desc_fr : p.short_desc_ar} | URL: ${p.product_url || 'N/A'}`;
        }).join('\n');

        const systemPrompt = `
You are a helpful sales assistant for an e-commerce store "Farhan" specializing in organic Moroccan products.
Answer in the following language: ${language === 'fr' ? 'French' : 'Arabic'}.
Use the provided product catalog to answer.

Rules:
1. If the user asks about a product in the catalog, answer their question.
2. If the user asks about a product NOT in the catalog, or asks for complex information you don't have, or asks to speak to a human:
   - You MUST apologize politely.
   - You MUST suggest they contact the support team via WhatsApp.
   - You MUST generate a "handoffSummary" which is a concise summary of their request in the first person (msg to be sent to support).

Output Format:
You must strictly output a valid JSON object. Do not include markdown formatting like \`\`\`json.
Structure:
{
    "text": "The response message to the user",
    "handoffSummary": "Optional string. Only include this if you couldn't answer.",
    "suggestions": "Optional array of objects each with { \"title\": \"Product Name\", \"url\": \"Product URL\" }. Include up to 3 main products you are suggesting."
}

Product Catalog:
${productContext}

User Question: ${userMessage}
`;

        const response = await axios.post(GEMINI_API_URL, {
            contents: [{
                parts: [{
                    text: systemPrompt
                }]
            }]
            // Removed generationConfig as gemini-pro (v1beta) often doesn't support response_mime_type consistently
        });

        const content = response.data?.candidates?.[0]?.content?.parts?.[0]?.text;

        if (!content) throw new Error("No content received");

        // More robust JSON extraction: find first '{' and last '}'
        const firstBrace = content.indexOf('{');
        const lastBrace = content.lastIndexOf('}');

        let jsonString = content;
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
            jsonString = content.substring(firstBrace, lastBrace + 1);
        }

        try {
            const parsed = JSON.parse(jsonString) as GeminiResponse;
            return parsed;
        } catch (e) {
            console.error("Failed to parse Gemini JSON:", content);
            // Fallback try to extract just text if JSON fails
            return { text: content };
        }

    } catch (error: any) {
        console.error("Error calling Gemini API:", error);

        if (error.response) {
            console.error("Gemini API Error Data:", error.response.data);
            console.error("Gemini API Status:", error.response.status);
        } else if (error.request) {
            console.error("Gemini API No Response:", error.request);
        } else {
            console.error("Gemini API Setup Error:", error.message);
        }

        let errorMessage = language === 'fr'
            ? "Désolé, je rencontre des problèmes techniques."
            : "عذراً، أواجه بعض المشاكل التقنية.";

        // Append technical debug info
        if (error.response) {
            errorMessage += `\n[Debug: Status ${error.response.status} - ${JSON.stringify(error.response.data?.error?.message || error.response.statusText)}]`;
        } else {
            errorMessage += `\n[Debug: ${error.message}]`;
        }

        return {
            text: errorMessage
        };
    }
};
